
//TODO [aosbornz] In an effort to make the code more organized, we are starting this file to put dashroll related code.
// The intent is to fully move all the logic over to this file once all functionality can be put in clean functions.
// Until such point, please move functions over one at a time until all business logic is in here.
// Please consider:
//  * Adding good unit tests for this.
//  * Adding strongly typed parameters and variables where possible.

import {
  IPNManufacturerPair,
  Model,
  MPNToIPNPair,
  ReferenceLineItemValidationCode,
  ValidationStatusType
} from '../../store/types';
import { sortByLastThree } from '../Utils/Utils';
import { UNKNOWN_MANUFACTURER_PLACEHOLDER, UNKNOWN_REFERENCE_LINE_ITEM_PLACEHOLDER } from '../../constants';

export const filterOutErrorAndPairingInvalidLineItems = (referenceLineItems, mpn) => {
  return referenceLineItems?.filter(
    (ref) =>
      (ref?.status?.validationStatusType !== ValidationStatusType.ERROR ||
        ref?.status?.statusCode === ReferenceLineItemValidationCode.ASN_MPN_IPN_PAIRING_INVALID) &&
      ref?.refereceLineItem?.expectedMPN?.toUpperCase() === mpn?.toUpperCase(),
  );
}

export const convertMpnMappingToReferenceLineItem = (associatedIpns: any[], mpn: string) => {
  return associatedIpns.map((associatedIpn: MPNToIPNPair) => {
    const model: Model = {
      modelNumber: mpn,
      expectedMPN: mpn,
      manufacturer: associatedIpn.manufacturer,
      description: associatedIpn.description,
      totalSerials: 0,
      expectedIPN: associatedIpn.ipn,
      mappedIPN: associatedIpn.ipn,
      expectedMaterialDefinitionId: associatedIpn.materialDefintionId,
    };

    return model;
  });
};

export const sortByReferenceItem = (elem1: any, elem2: any) => {
  if (elem1?.refereceLineItem?.expectedIPN?.length <= 10 || elem2?.refereceLineItem?.expectedIPN?.length <= 10) {
    return 0;
  }
  return sortByLastThree(elem1?.refereceLineItem, elem2?.refereceLineItem, 'expectedIPN');
}

export const sortByIPN = (elem1: any, elem2: any) => {
  if (elem1?.ipn?.length <= 10 || elem2?.ipn?.length <= 10) {
    return 0;
  }
  return sortByLastThree(elem1, elem2, 'ipn');
}

export const sortByModelIPN = (elem1: any, elem2: any) => {
  if (elem1?.expectedIPN?.length <= 10 || elem2?.expectedIPN?.length <= 10) {
    return 0;
  }
  return sortByLastThree(elem1, elem2, 'expectedIPN');
}

const filterIpnsByDashroll = (mpnIPNPairingTownsendList): { [p: string]: string } => {
  const sortedIpns = [...mpnIPNPairingTownsendList].sort(sortByModelIPN);

  const ipnBasemap: { [p: string]: string } = {};
  sortedIpns.forEach(townsendPair => {
    const ipn = townsendPair?.expectedIPN
    const ipnBase = ipn?.slice(0, 10);

    if (ipn === ipnBase) {
      // If the IPN is equal to the IPN based assign it to the list (even if it overwrites the value
      ipnBasemap[ipnBase] = ipn;
    } else if (ipnBasemap[ipnBase]) {
      return;
    } else {
      ipnBasemap[ipnBase] = ipn;
    }
  });

  return ipnBasemap;
}

export const getCommonLineItemAndIpnLists = (mappedReferenceLineItems, mpnIPNPairingTownsendList, manufacturer): {
  ipnList: any[],
  lineItemList: any[],
  matchingLineItemsFound: boolean,
} => {
  let matchingLineItemsFound: boolean = true;
  //Create filtered IPN townsend list that has only unique dashrolled IPNs
  const filteredIpnList = filterIpnsByDashroll(mpnIPNPairingTownsendList);

  // Collect a list of exact and dashrolled matches as well as filtered out count.
  let filteredOutLineItems: number = 0;
  const exactIpnMatchList: IPNManufacturerPair[] = [];
  const dashrollIpnMatchList: IPNManufacturerPair[] = [];
  // Filter out line items that do not have an exact match
  const filteredLineItems = mappedReferenceLineItems?.filter(lineItem => {
    const exactMatch = mpnIPNPairingTownsendList.find(ipn =>
      ipn?.expectedIPN === lineItem?.expectedIPN)?.expectedIPN;
    const ipnBase = lineItem?.expectedIPN?.slice(0, 10);

    const keepInFilter = exactMatch ? exactMatch : filteredIpnList[ipnBase];

    if (exactMatch) {
      // If Exact match add to exact match list
      exactIpnMatchList.push({
        ipn: exactMatch,
        manufacturer: manufacturer,
      });
    } else if (filteredIpnList[ipnBase]) {
      // If Dashroll match add to exact match list
      dashrollIpnMatchList.push({
        ipn: filteredIpnList[ipnBase],
        manufacturer: manufacturer,
      });
    } else {
      // If filtered out add +1 to filtered line item count
      filteredOutLineItems += 1;
    }

    return keepInFilter;
  });

  // Apply Exact or Dashrolled matches to the line items that can have them.
  const updatedLineItems = filteredLineItems?.map(lineItem => {
    const exactMatch = mpnIPNPairingTownsendList.find(ipn =>
      ipn?.expectedIPN === lineItem?.expectedIPN)?.expectedIPN;
    const ipnBase = lineItem?.expectedIPN?.slice(0, 10);
    const mappedIpn = exactMatch ? exactMatch : filteredIpnList[ipnBase];

    return {
      ...lineItem,
      /*
          expectedIpn is townsend IPN / what is on the ASN
          mappedIpn is financial reconciliation / what is passed through to the receive service
      */
      mappedIPN: mappedIpn
    }
  });

  const ipnSet = new Set();
  exactIpnMatchList.forEach(lineItem => ipnSet.add(lineItem));
  dashrollIpnMatchList.forEach(lineItem => ipnSet.add(lineItem));

  if (filteredOutLineItems > 0 || filteredLineItems?.length === 0) {
    Object.entries(filteredIpnList).forEach(ipn => ipnSet.add({
      ipn: ipn[1],
      manufacturer: manufacturer,
    }));
    matchingLineItemsFound = false;
  }
  return {
    ipnList: filterDuplicateEntriesByIPNAndManufacturer(Array.from(ipnSet)),
    lineItemList: updatedLineItems,
    matchingLineItemsFound: matchingLineItemsFound,
  };
}

/**
 * Returns true iff there exists more than one manufacturer in the provided list of MPNToIPNPair's. Returns false
 * otherwise.
 * @param mpnToIpnPairList list of MPNToIPNPair to analyze
 */
export const areMultipleManufacturersPresent = (mpnToIpnPairList: MPNToIPNPair[] | undefined): boolean => {
  if (!mpnToIpnPairList) {
    return false;
  }
  const foundManufacturers: Set<string | undefined> = new Set();
  mpnToIpnPairList.forEach((mpnToIpnPair: MPNToIPNPair): void => {
    foundManufacturers.add(mpnToIpnPair.manufacturer)
  });
  return foundManufacturers.size > 1;
};


/**
 * Returns a mapping from manufacturer to a list of associated Model entries for that manufacturer.
 */
export const getManufacturerToAssociatedModelsMapping = (models: Model[]): Map<string, Model[]> => {
  const manufacturerToIpnListMapping: Map<string, Model[]> = new Map();

  models.forEach((model: Model): void => {
    const manufacturer: string = model.manufacturer ? model.manufacturer : UNKNOWN_MANUFACTURER_PLACEHOLDER;
    let modelList: Model[] | undefined = manufacturerToIpnListMapping.get(manufacturer);

    if (modelList) {
      modelList.push(model);
      // Case: existing mapping entry found; add to list.
      manufacturerToIpnListMapping.set(manufacturer, modelList)
    } else {
      // Case: new entry found; create new list.
      manufacturerToIpnListMapping.set(manufacturer, [model]);
    }
  });
  return manufacturerToIpnListMapping;
}

/**
 * Returns a new list containing the entries of the provided list with no two entries that have the same
 * referenceLineItemId.
 */
export const filterDuplicateLineItemsByReferenceLineItemId = (referenceLineItems: any[]): any[] => {
  const foundReferenceLineItems: Map<string, any> = new Map<string, any>();
  referenceLineItems.forEach((referenceLineItemEntry): void => {
    const referenceLineItemId: string = referenceLineItemEntry?.referenceLineItemId
      ? referenceLineItemEntry.referenceLineItemId : UNKNOWN_REFERENCE_LINE_ITEM_PLACEHOLDER;
    if (!foundReferenceLineItems.has(referenceLineItemId)) {
      foundReferenceLineItems.set(referenceLineItemId, referenceLineItemEntry);
    }
  });
  return Array.from(foundReferenceLineItems.values());
}

/**
 * Returns a new array where any duplicate items (identified by their ipn and manufacturer values)
 * are filtered out.
 */
const filterDuplicateEntriesByIPNAndManufacturer = (entrySet: any[]): any[] => {
  let foundItemsTrackingList = [];
  let filteredList = [];
  entrySet.forEach((entry) => {
    const seen: boolean = foundItemsTrackingList.find((foundItemEntry) =>
      foundItemEntry?.ipn === entry?.ipn && foundItemEntry.manufacturer === entry?.manufacturer
    );
    if (!seen) {
      foundItemsTrackingList = foundItemsTrackingList.concat(entry);
      filteredList = filteredList.concat(entry);
    }
  });
  return filteredList;
}

/**
 * Return if the provided IPNs have the same base (are dashroll matches)
 * @param ipn1
 * @param ipn2
 */
export const isDashrollMatch = (ipn1?: string, ipn2?: string): boolean => {
  const ipnBase1 = ipn1?.slice(0, 10);
  const ipnBase2 = ipn2?.slice(0, 10);

  return ipnBase1 === ipnBase2 && !!ipnBase1;
}
